import React, {
  createContext,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  useState,
} from "react";
import { SearchUnit, createUnitId } from "./HeaderContext";

/** Chosen package category ad per staged unit id; null means "not chosen yet". */
export type ChosenCategoryByUnitId = Record<string, string | null>;

interface RoomInfoSelectionContextType {
  startDate: Date | null;
  endDate: Date | null;
  currentMonth: Date;
  units: SearchUnit[];
  activeUnitId: string;
  /** Package mode only. Lives here rather than in PackageDetailDialog because
   *  the mobile search sheet offers the same choice as its own step, and both
   *  surfaces must write to one source of truth. */
  chosenCategoryByUnit: ChosenCategoryByUnitId;

  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  setCurrentMonth: (date: Date) => void;
  setUnits: React.Dispatch<React.SetStateAction<SearchUnit[]>>;
  setActiveUnitId: (id: string) => void;
  setChosenCategoryByUnit: React.Dispatch<
    React.SetStateAction<ChosenCategoryByUnitId>
  >;

  seedSelection: (input: SeedRoomInfoSelectionInput) => void;
  resetSelection: () => void;
}

export interface SeedRoomInfoSelectionInput {
  startDate?: Date | null;
  endDate?: Date | null;
  currentMonth?: Date | null;
  units?: SearchUnit[];
  activeUnitId?: string;
}

const RoomInfoSelectionContext = createContext<
  RoomInfoSelectionContextType | undefined
>(undefined);

const createDefaultSelection = () => {
  const id = createUnitId();
  const unit: SearchUnit = {
    id,
    guests: [{ ageType: "adult", bedCapacityType: "full" }],
  };
  return {
    startDate: null as Date | null,
    endDate: null as Date | null,
    currentMonth: new Date(),
    units: [unit],
    activeUnitId: id,
  };
};

const cloneUnits = (units: SearchUnit[]): SearchUnit[] =>
  units.map((unit) => ({
    ...unit,
    guests: unit.guests.map((guest) => ({ ...guest })),
  }));

export const RoomInfoSelectionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const initial = useMemo(() => createDefaultSelection(), []);
  const [startDate, setStartDate] = useState<Date | null>(initial.startDate);
  const [endDate, setEndDate] = useState<Date | null>(initial.endDate);
  const [currentMonth, setCurrentMonth] = useState<Date>(initial.currentMonth);
  const [units, setUnits] = useState<SearchUnit[]>(initial.units);
  const [activeUnitId, setActiveUnitId] = useState<string>(initial.activeUnitId);
  const [chosenCategoryByUnit, setChosenCategoryByUnit] =
    useState<ChosenCategoryByUnitId>({});

  const seedSelection = useCallback((input: SeedRoomInfoSelectionInput) => {
    const defaults = createDefaultSelection();
    const seededUnits = input.units?.length
      ? cloneUnits(input.units)
      : defaults.units;
    const seededActiveId =
      input.activeUnitId &&
      seededUnits.some((unit) => unit.id === input.activeUnitId)
        ? input.activeUnitId
        : seededUnits[0]!.id;
    setStartDate(input.startDate ?? defaults.startDate);
    setEndDate(input.endDate ?? defaults.endDate);
    setCurrentMonth(
      input.currentMonth ?? input.startDate ?? defaults.currentMonth,
    );
    setUnits(seededUnits);
    setActiveUnitId(seededActiveId);
    // A reseed is a new dialog session; category choices never carry over.
    setChosenCategoryByUnit({});
  }, []);

  // Drop choices for units that no longer exist. `setUnits` is exposed directly
  // and every caller that removes a room went through it, so the choices
  // outlived their unit — harmless while nothing read them, and exactly the
  // kind of stale state that later reads wrongly.
  useEffect(() => {
    setChosenCategoryByUnit((previous) => {
      const liveUnitIds = new Set(units.map((unit) => unit.id));
      const staleIds = Object.keys(previous).filter((id) => !liveUnitIds.has(id));
      if (staleIds.length === 0) return previous;
      const next = { ...previous };
      for (const id of staleIds) delete next[id];
      return next;
    });
  }, [units]);

  const resetSelection = useCallback(() => {
    const defaults = createDefaultSelection();
    setStartDate(defaults.startDate);
    setEndDate(defaults.endDate);
    setCurrentMonth(defaults.currentMonth);
    setUnits(defaults.units);
    setActiveUnitId(defaults.activeUnitId);
    setChosenCategoryByUnit({});
  }, []);

  const value = useMemo<RoomInfoSelectionContextType>(
    () => ({
      startDate,
      endDate,
      currentMonth,
      units,
      activeUnitId,
      chosenCategoryByUnit,
      setStartDate,
      setEndDate,
      setCurrentMonth,
      setUnits,
      setActiveUnitId,
      setChosenCategoryByUnit,
      seedSelection,
      resetSelection,
    }),
    [
      startDate,
      endDate,
      currentMonth,
      units,
      activeUnitId,
      chosenCategoryByUnit,
      seedSelection,
      resetSelection,
    ],
  );

  return (
    <RoomInfoSelectionContext.Provider value={value}>
      {children}
    </RoomInfoSelectionContext.Provider>
  );
};

// oxlint-disable-next-line react/only-export-components -- tests mock this module path for the hook
export const useRoomInfoSelection = () => {
  const ctx = useContext(RoomInfoSelectionContext);
  if (!ctx) {
    throw new Error(
      "useRoomInfoSelection must be used within a RoomInfoSelectionProvider",
    );
  }
  return ctx;
};

// Non-throwing variant for components that may be rendered outside the
// provider (MobileSearchSheet is also used in plain search mode where the
// provider may not be mounted).
// oxlint-disable-next-line react/only-export-components -- tests mock this module path for the hook
export const useRoomInfoSelectionOrNull = () =>
  useContext(RoomInfoSelectionContext);
